import React, { useState, useEffect, useRef } from "react";
import { Spin } from "antd";
import { FullscreenOutlined, FullscreenExitOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Card, CardBody, CardHeader } from "reactstrap";

const CardImageItem = ({ files = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const imageContainerRef = useRef(null);
  
  const totalImages = files.length;
  const currentFileName = files[currentIndex];
  const ImgName = currentFileName ? currentFileName.split("--").pop() : "";

  const handleImageLoad = () => setLoading(false);
  const handleImageError = () => setLoading(false);

  const changeImage = (direction) => {
    setLoading(true);
    setCurrentIndex((prevIndex) => Math.min(Math.max(prevIndex + direction, 0), totalImages - 1));
  };

  const toggleFullscreen = () => {
    if (isFullscreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    } else {
      if (imageContainerRef.current.requestFullscreen) {
        imageContainerRef.current.requestFullscreen();
      } else if (imageContainerRef.current.mozRequestFullScreen) {
        imageContainerRef.current.mozRequestFullScreen();
      } else if (imageContainerRef.current.webkitRequestFullscreen) {
        imageContainerRef.current.webkitRequestFullscreen();
      } else if (imageContainerRef.current.msRequestFullscreen) {
        imageContainerRef.current.msRequestFullscreen();
      }
    }
    setIsFullscreen((prev) => !prev);
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      if (
        document.fullscreenElement ||
        document.mozFullScreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      ) {
        setIsFullscreen(true);
      } else {
        setIsFullscreen(false);
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("msfullscreenchange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", handleFullscreenChange);
    };
  }, []);

  if (totalImages === 0 || !currentFileName) {
    return (
      <Card className="card-animate">
        <CardBody><p className="text-center">Pas d'images pour le moment 😑</p></CardBody>
      </Card>
    );
  }

  return (
    <Card className="card-animate">
      <CardHeader>
        {currentFileName && (
          <div className="image-header d-flex justify-content-between">
            <div className="image-name text-muted">{ImgName}</div>
            <div className="image-counter text-muted">{currentIndex + 1}/{totalImages}</div>
          </div>
        )}
      </CardHeader>
      <CardBody size="small">
        <div
          ref={imageContainerRef}
          className="image-container"
          style={{
            position: "relative",
            textAlign: isFullscreen ? "center" : "initial",
            height: isFullscreen ? "100vh" : "auto",
          }}
        >
          {loading && (
            <div className="loading-spinner mt-3">
              <p>Chargement, merci de patienter...</p>
              <Spin size="large" className="text-center" />
            </div>
          )}

          {currentFileName && (
            <>
              <div className="d-flex justify-content-center align-items-center" style={{ height: isFullscreen ? "100vh" : "40vh" }}>
                <img
                  src={`${process.env.REACT_APP_API_MEDIA_URL}/print/${currentFileName}`}
                  alt="Image"
                  className="img-fluid"
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  style={{
                    display: loading ? "none" : "block",
                    width: isFullscreen ? "100%" : "50%",
                    height: isFullscreen ? "100vh" : "50vh",
                    objectFit: "contain",
                    margin: isFullscreen ? "auto" : "initial",
                  }}
                />
              </div>

              <div
                onClick={toggleFullscreen}
                style={{
                  position: "absolute", top: "10px", right: "10px", fontSize: "24px", cursor: "pointer", zIndex: 2
                }}
              >
                {isFullscreen ? <FullscreenExitOutlined title="Réduire la photo"/> : <FullscreenOutlined title="Agrandir la photo"/>}
              </div>
            </>
          )}

          {totalImages > 1 && currentIndex > 0 && (
            <LeftOutlined
              onClick={() => changeImage(-1)}
              style={{ position: "absolute", top: "50%", left: "10px", fontSize: "24px", cursor: "pointer", zIndex: 2, transform: "translateY(-50%)" }}
            />
          )}

          {totalImages > 1 && currentIndex < totalImages - 1 && (
            <RightOutlined
              onClick={() => changeImage(1)}
              style={{ position: "absolute", top: "50%", right: "10px", fontSize: "24px", cursor: "pointer", zIndex: 2, transform: "translateY(-50%)" }}
            />
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default CardImageItem;
